// DocumentTemplateAPI.ts - API service for document templates
// Templates are created in Admin Settings → Document Management → Document Templates
// This service is used to READ templates for selection in DMSRightPanel

import { useKeycloak } from '@react-keycloak/web';
import { useMemo } from 'react';

export interface DocumentTemplate {
  template_id: string;
  name: string;
  description?: string;
  template_type: 'contract' | 'pleading' | 'correspondence' | 'form' | 'research' | 'discovery' | 'other';
  status: 'active' | 'inactive' | 'archived';
  is_enabled: boolean;
  practice_areas?: string[];
  template_content?: string;
  content_format?: 'html' | 'markdown' | 'lexical' | 'plaintext';
  template_variables?: Record<string, any>;
  template_metadata?: Record<string, any>;
  is_default: boolean;
  usage_count?: number;
  last_used_at?: Date;
}

export interface DocumentTemplateFilterOptions {
  template_type?: string;
  status?: 'active' | 'inactive' | 'archived';
  practice_area?: string;
  is_enabled?: boolean;
  search?: string;
  include_usage_count?: boolean;
}

export interface DocumentTemplateResponse {
  message: string;
  templates: DocumentTemplate[];
  total: number;
  filters_applied?: DocumentTemplateFilterOptions;
}

// Map DocumentCategory.category_type to DocumentTemplate.template_type
// This mapping is used to filter templates based on selected document category
export const categoryTypeToTemplateType: Record<string, string> = {
  'CONTRACT': 'contract',
  'PROCEDURAL_FILING': 'pleading',
  'CORRESPONDENCE': 'correspondence',
  'DISCOVERY': 'discovery',
  'CORPORATE_BUSINESS': 'contract', // Could also be 'form'
  'FAMILY_LAW': 'pleading',
  'FINANCE_REAL_PROPERTY': 'contract',
  'CRIMINAL': 'pleading',
  'ADMINISTRATIVE': 'form',
  'ESTATE_PROBATE': 'form',
  'LEGAL_OPINION': 'research',
  'NEGOTIATION_DRAFT': 'contract',
  // Default fallback
  'OTHER': 'other'
};

export const useDocumentTemplateAPI = () => {
  const { keycloak } = useKeycloak();

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${keycloak.token}`,
    'Content-Type': 'application/json',
  });

  const baseUrl = window.location.origin;

  /**
   * Get templates with optional filtering
   * Used in DMSRightPanel to display available templates for document creation
   */
  const getTemplates = async (filters?: DocumentTemplateFilterOptions): Promise<DocumentTemplate[]> => {
    try {
      const searchParams = new URLSearchParams();
      
      if (filters?.template_type) {
        searchParams.append('template_type', filters.template_type);
      }
      if (filters?.status) {
        searchParams.append('status', filters.status);
      }
      if (filters?.practice_area) {
        searchParams.append('practice_area', filters.practice_area);
      }
      if (filters?.is_enabled !== undefined) {
        searchParams.append('is_enabled', filters.is_enabled.toString());
      }
      if (filters?.search) {
        searchParams.append('search', filters.search);
      }
      if (filters?.include_usage_count !== undefined) {
        searchParams.append('include_usage_count', filters.include_usage_count.toString());
      }

      const url = `${baseUrl}/api/v1/dms/admin/templates${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
      
      const response = await fetch(url, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        // Handle 403 (admin access required) gracefully
        if (response.status === 403) {
          console.warn('Template API requires admin access. Templates will not be available.');
          return [];
        }
        throw new Error(`Failed to fetch templates: ${response.statusText}`);
      }

      const data: DocumentTemplateResponse = await response.json();
      return data.templates || [];
    } catch (error) {
      console.error('Error fetching templates:', error);
      // Return empty array on error to allow document creation without templates
      return [];
    }
  };

  /**
   * Get a single template by ID
   */
  const getTemplateById = async (templateId: string): Promise<DocumentTemplate | null> => {
    try {
      const response = await fetch(
        `${baseUrl}/api/v1/dms/admin/templates/${templateId}`,
        { headers: getAuthHeaders() }
      );

      if (!response.ok) {
        if (response.status === 403) {
          console.warn('Template API requires admin access.');
          return null;
        }
        throw new Error(`Failed to fetch template: ${response.statusText}`);
      }

      const data = await response.json();
      return data.template || null;
    } catch (error) {
      console.error('Error fetching template:', error);
      return null;
    }
  };

  /**
   * Map category type to template type for filtering
   */
  const getTemplateTypeForCategory = (categoryType: string): string => {
    return categoryTypeToTemplateType[categoryType] || 'other';
  };

  return useMemo(() => ({
    getTemplates,
    getTemplateById,
    getTemplateTypeForCategory,
    categoryTypeToTemplateType,
  }), [keycloak.token]);
};

